import React from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
	EuiButton,
	EuiEmptyPrompt,
	EuiFlexGroup,
	EuiFlexItem,
	EuiGlobalToastList,
	EuiLoadingSpinner,
	EuiPage,
	EuiPageBody,
	EuiPageHeader,
	EuiSpacer,
	EuiTitle,
} from '@equipmentshare/ds2'
import { useDemandRequestQuery, useCompanyQuery, Permission } from '../../graphql'
import { LOADING_CONTAINER_MIN_HEIGHT } from './RequestDetails.constants'
import { LocationState } from './types'
import { copyToClipboard } from '../../utils/clipboard'
import { useToastNotifications } from '../../hooks/useToastNotifications'
import { TOAST_DURATION_MS } from '../../config/toasts'
import { Toast } from '../../types/toast'
import RequestInfoPanel from './components/RequestInfoPanel'
import FulfillmentPanel from './components/FulfillmentPanel'
import RequestedItemsTable from './components/RequestedItemsTable'
import NotesModal from './components/NotesModal'
import CreateFulfillmentOrderModal from './components/CreateFulfillmentOrderModal'
import usePermissions from '../../hooks/usePermissions'
import { checkPermissions } from '../../components/PermissionsCheck/helpers'
import {
	useLineItemSelection,
	useLineItemActions,
	useBulkLineItemActions,
	useFulfillerNotes,
	usePurchaseOrderCreation,
	useTransferCreation,
	useMarkRequestAsCompleted,
	useFulfillmentOrderCreation,
} from './RequestDetails.hooks'
import { useMarketTransferOrderFlag } from '../transfer-orders/hooks/useMarketTransferOrderFlag'
import { useFlags } from '@equipmentshare/feature-flags'

export default function RequestDetails() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const location = useLocation()
	const { toasts, addToast, removeToast } = useToastNotifications()

	// The requests list keeps its filters, page and row size in its query string,
	// so returning to it means returning to that exact URL. The list row Link
	// hands it over in router state; every other way into this view (the transfer
	// orders grid, a transfer order detail, the create-transfer-order form, a deep
	// link) has no marker and falls back to the bare list.
	const requestsHref = ( location.state as LocationState | null )?.from ?? '/requests'

	// Check permissions for editing demand requests
	const { userPermissions } = usePermissions( [ Permission.CanEditDemandRequestByStoreId, Permission.CanCreateFulfillmentOrderByStoreId ] )
	const canUserEditRequests = checkPermissions( userPermissions, [ Permission.CanEditDemandRequestByStoreId ] )
	const canUserCreateFulfillmentOrder = checkPermissions( userPermissions, [ Permission.CanCreateFulfillmentOrderByStoreId ] )

	// Costentory:EnableDemandSourcing gates the available-qty column, fulfillment
	// status badge column, and the sourcing artifact drill-down.
	const flags = useFlags()
	const enableDemandSourcing = Boolean( flags.costentoryEnableDemandSourcing )

	const { data, loading, error } = useDemandRequestQuery( {
		variables: {
			id: id ?? '0',
		},
		skip: !id,
		fetchPolicy: 'network-only',
	} )

	const request = data?.demandRequest

	// Shipping recipient defaults come from the demand request's requesting user
	// (the person who asked for the goods), not the logged-in fulfiller. The
	// name comes straight off the request; the company name is resolved from
	// es-api by the requesting user's company id, on page load, so the modal
	// opens pre-filled.
	const requestingUser = request?.requestedByUser
	const defaultRecipientName = requestingUser
		? [ requestingUser.firstName, requestingUser.lastName ].filter( Boolean ).join( ' ' ) || undefined
		: undefined

	const { data: companyData } = useCompanyQuery( {
		variables: { companyId: requestingUser?.companyId ?? '' },
		skip: !requestingUser?.companyId,
	} )
	const defaultCompanyName = companyData?.company?.name ?? undefined

	// Custom hooks for managing different workflows
	const {
		selectedLineItemIds,
		selectionVersion,
		handleSelectLineItem,
		clearSelection,
	} = useLineItemSelection()

	const {
		handleCompleteLineItem,
		handleReopenLineItem,
		handleCancelLineItem,
	} = useLineItemActions( { request, addToast } )

	const {
		isBulkActionLoading,
		handleBulkCompleteLineItems,
		handleBulkReopenLineItems,
		handleBulkCancelLineItems,
	} = useBulkLineItemActions( {
		request,
		selectedLineItemIds,
		addToast,
		clearSelection,
	} )

	const {
		fulfillerNotesModalLineItemId,
		currentLineItem,
		handleEditFulfillerNote,
		handleSaveFulfillerNote,
		handleCloseNotesModal,
	} = useFulfillerNotes( { request, addToast } )

	const { handleCreatePurchaseOrder } = usePurchaseOrderCreation( {
		request,
		selectedLineItemIds,
		addToast,
	} )

	const { useNewFlowRef, isLoading: isTransferFlagLoading } = useMarketTransferOrderFlag(
		request?.requestingInventory?.branchId
	)

	const { handleCreateTransfer } = useTransferCreation( {
		request,
		selectedLineItemIds,
		useNewFlowRef,
		addToast,
		clearSelection,
	} )

	const { handleMarkRequestAsCompleted } = useMarkRequestAsCompleted( { request, addToast } )

	// TODO: extract this block into a dedicated hook
	const { submit: submitFulfillmentOrder, isSubmitting: isFulfillmentOrderSubmitting } = useFulfillmentOrderCreation( {
		request,
		addToast,
		clearSelection,
	} )

	const [ isFulfillmentOrderModalOpen, setIsFulfillmentOrderModalOpen ] = React.useState( false )

	const selectedLineItems = React.useMemo( () => {
		if ( !request ) {
			return []
		}
		return selectedLineItemIds
			.map( ( lineItemId ) => request.lineItems.find( ( item ) => item.lineItemId === lineItemId ) )
			.filter( ( item ): item is NonNullable<typeof item> => item !== undefined )
	}, [ request, selectedLineItemIds ] )

	const handleCreateFulfillmentOrder = () => {
		if ( selectedLineItems.length === 0 ) {
			return
		}
		setIsFulfillmentOrderModalOpen( true )
	}

	const handleFulfillmentOrderModalSubmit = async(
		params: Parameters<typeof submitFulfillmentOrder>[0],
	) => {
		const result = await submitFulfillmentOrder( params )
		if ( result.success ) {
			setIsFulfillmentOrderModalOpen( false )
		}
	}
	// END TODO

	const handleBackClick = () => {
		navigate( requestsHref )
	}

	const handleCopyToClipboard = async( text: string, label: string ) => {
		const result = await copyToClipboard( text )
		if ( result.success ) {
			addToast( `${label} copied to clipboard` )
		}
	}

	if ( loading ) {
		return (
			<EuiPage paddingSize="l">
				<EuiPageBody>
					<EuiFlexGroup justifyContent="center" alignItems="center" style={{ minHeight: LOADING_CONTAINER_MIN_HEIGHT }}>
						<EuiFlexItem grow={false}>
							<EuiLoadingSpinner size="xl" />
						</EuiFlexItem>
					</EuiFlexGroup>
				</EuiPageBody>
			</EuiPage>
		)
	}

	if ( error || !request ) {
		return (
			<EuiPage paddingSize="l">
				<EuiPageBody>
					<EuiEmptyPrompt
						color="danger"
						iconType="alert"
						title={<h2>Error loading request</h2>}
						body={<p>{error?.message ?? 'Request not found'}</p>}
						actions={
							<EuiButton onClick={handleBackClick}>
								Back to Requests
							</EuiButton>
						}
					/>
				</EuiPageBody>
			</EuiPage>
		)
	}

	return (
		<>
			<EuiPage paddingSize="l">
				<EuiPageBody>
					<EuiPageHeader
						pageTitle={<EuiTitle size="l"><h1>REQ-{request?.requestNumber ?? id}</h1></EuiTitle>}
						breadcrumbs={[
							{ text: 'Requests',
								href: requestsHref,
								onClick: ( e ) => {
									// Let cmd/ctrl/shift-click and middle-click fall through to the real
									// href, which points at the same list this would navigate to.
									if ( e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0 ) {
										return
									}
									e.preventDefault()
									handleBackClick()
								} },
							{ text: 'Request Details' },
						]}
					/>
					<EuiSpacer size="l" />

					{/* Side by Side Panels */}
					<div>
						<EuiFlexGroup gutterSize="s" alignItems="stretch" wrap={false}>
							{/* Left Panel: Request Information */}
							<EuiFlexItem>
								<RequestInfoPanel request={request} onCopy={handleCopyToClipboard} />
							</EuiFlexItem>

							{/* Right Panel: Fulfillment, Status & Actions */}
							<EuiFlexItem>
								<FulfillmentPanel request={request} canUserEditRequests={canUserEditRequests} onMarkAsCompleted={handleMarkRequestAsCompleted} />
							</EuiFlexItem>
						</EuiFlexGroup>
					</div>

					<EuiSpacer size="s" />

					{/* Requested Items */}
					<RequestedItemsTable
						items={request.lineItems ?? []}
						fulfillingInventory={request.owningInventory}
						showFulfillingInventoryStock={request.fulfillingInventory != null}
						selectedLineItemIds={selectedLineItemIds}
						selectionVersion={selectionVersion}
						isBulkActionLoading={isBulkActionLoading}
						canUserEditRequests={canUserEditRequests}
						onSelectLineItem={handleSelectLineItem}
						onCreatePurchaseOrder={handleCreatePurchaseOrder}
						onCreateTransfer={handleCreateTransfer}
						isTransferFlagLoading={isTransferFlagLoading}
						onCreateFulfillmentOrder={handleCreateFulfillmentOrder}
						canUserCreateFulfillmentOrder={canUserCreateFulfillmentOrder}
						enableDemandSourcing={enableDemandSourcing}
						onBulkCompleteLineItems={handleBulkCompleteLineItems}
						onBulkReopenLineItems={handleBulkReopenLineItems}
						onBulkCancelLineItems={handleBulkCancelLineItems}
						onCompleteLineItem={handleCompleteLineItem}
						onReopenLineItem={handleReopenLineItem}
						onCancelLineItem={handleCancelLineItem}
						onEditFulfillerNote={handleEditFulfillerNote}
						onCopy={handleCopyToClipboard}
					/>
					<EuiSpacer size="l" />
				</EuiPageBody>
			</EuiPage>

			{/* Create Fulfillment Order Modal */}
			<CreateFulfillmentOrderModal
				isOpen={isFulfillmentOrderModalOpen}
				onClose={() => setIsFulfillmentOrderModalOpen( false )}
				selectedLineItems={selectedLineItems}
				requestingInventoryId={request?.requestingInventory?.id}
				requestingInventoryBranchId={request?.requestingInventory?.branchId}
				requestingInventoryIsMtt={request?.requestingInventory?.branchV2?.isMtt}
				deliveryAddress={request?.deliveryAddress}
				defaultContactPhone={request?.contactPhonePrimary}
				defaultRecipientName={defaultRecipientName}
				defaultCompanyName={defaultCompanyName}
				defaultFulfillingInventoryId={request.owningInventory.id}
				defaultFulfillingInventoryName={request.owningInventory.name}
				defaultFulfillingInventoryIsWarehouse={request.owningInventory.isWarehouse}
				isSubmitting={isFulfillmentOrderSubmitting}
				onSubmit={handleFulfillmentOrderModalSubmit}
				canEditFulfillerNotes={canUserEditRequests}
			/>

			{/* Fulfiller Notes Modal */}
			<NotesModal
				isOpen={!!fulfillerNotesModalLineItemId}
				onClose={handleCloseNotesModal}
				notes={currentLineItem?.fulfillerNotes ?? ''}
				onSave={handleSaveFulfillerNote}
				title={currentLineItem?.fulfillerNotes ? 'Edit Fulfillment Note' : 'Add Fulfillment Note'}
				placeholder="Enter your note to the requester here..."
			/>

			<EuiGlobalToastList
				toasts={toasts}
				dismissToast={( toast: NonNullable<React.ComponentProps<typeof EuiGlobalToastList>['toasts']>[number] ) => {
					// EUI's Toast type has title as ReactNode, while our Toast type has title as string
					// Since our toasts always have string titles, we can safely cast here
					removeToast( toast as Toast )
				}}
				toastLifeTimeMs={TOAST_DURATION_MS}
			/>
		</>
	)
}
